<script setup lang="ts">
import { storeToRefs } from 'pinia'

import AuthCreateBaseUser from '~/components/auth/CreateBaseUser.vue'
import InfluencerCreateBase from '~/components/influencer/Create/Base.vue'
import InfluencerCreateFavoriteArtists from '~/components/influencer/Create/FavoriteArtists.vue'
import InfluencerCreateLink from '~/components/influencer/Create/Link.vue'
import InfluencerTagsSelectPreference from '~/components/influencer/signup/selectPreferences.vue'
import SeoFooter from '~/components/seo/footer/index.vue'
import UploadPictureCard from '~/components/shared/uploadPictureCard.vue'

import { useSegmentIdentify } from '~/composables/Segment/useSegmentIdentify'
import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useCleanCache } from '~/composables/useCleanCache'
import { useGetMediaUrl } from '~/composables/useGetMediaUrl'
import { useGetSeoGenericCuratorList } from '~/composables/useGetSeoGenericCuratorList'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useInfluencerOnboardingFlowStore } from '~/stores/influencerOnboardingFlow'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserInfluencerStore } from '~/stores/userInfluencer'
import { useUserInfluencerTagsStore } from '~/stores/userInfluencerTags'

import { isFeatureOnForGroup } from '~/helpers/GrowthBookHelpers'
import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { provideCuratorRefreshCache } from '~/api-core/Curators/CuratorRefreshCache'
import { providePatchCurator } from '~/api-core/Curators/PatchCurator'

import type Tag from '~/entities/tag'
import type { UserState } from '~/stores/user'
import type { UserInfluencerState } from '~/stores/userInfluencer'

const { $growthBook } = useNuxtApp()
const { cleanCacheAll } = useCleanCache()
const { getMediaUrl } = useGetMediaUrl()
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { coreFetch } = useProvideCoreFetch()
const segmentIdentify = useSegmentIdentify()
const segmentTrack = useSegmentTrack()

const { FETCH: FETCH_TAGS, GET_TAGS_FROM_IDS, GET_TAG_FROM_ID } = useTagStore()
const { FETCH_OPPORTUNITIES, GET_CURATOR_TYPE_TAG } = useOpportunitiesStore()
const { FETCH: INFLUENCER_TAGS_FETCH } = useUserInfluencerTagsStore()

const newUser = ref<null | UserState>(null)
const newInfluencer = ref<null | UserInfluencerState>(null)
const state = ref(0)

const { CURRENT_STEP } = storeToRefs(useInfluencerOnboardingFlowStore())

const userInfluencerStore = useUserInfluencerStore()
const { id: INFLUENCER_ID } = storeToRefs(userInfluencerStore)
const { SET: USER_INFLUENCER_SET } = userInfluencerStore

const userInfluencerTagStore = useUserInfluencerTagsStore()
const INFLUENCER_TAGS = computed(() => userInfluencerTagStore.$state)

const { SET: USER_SET } = useUserStore()

definePageMeta({
  middleware: function () {
    const { $localePath, $pinia } = useNuxtApp()
    const userStore = useUserStore($pinia)

    if (userStore.IS_LOGGED_IN && !userStore.IS_INCOMPLETE_USER)
      return navigateTo($localePath('/'))
  },
})

await Promise.all([
  useAsyncData(() => INFLUENCER_TAGS_FETCH()),
  callOnce('FETCH_TAGS', FETCH_TAGS),
  callOnce('FETCH_OPPORTUNITIES', FETCH_OPPORTUNITIES),
])

useHead(() => {
  const title = t('metaTags.influencerSignup.title')
  const description = t('metaTags.influencerSignup.description')

  return {
    title,
    meta: [
      {
        name: 'description',
        key: 'description',
        content: description,
      },
      {
        key: 'og:title',
        property: 'og:title',
        content: title,
      },
      {
        key: 'og:description',
        property: 'og:description',
        content: description,
      },
    ],
  }
})

const { data: seoListing } = await useGetSeoGenericCuratorList()

onMounted(() => {
  manageState()
  window.localStorage.setItem(
    'influencerSignupTimestamp',
    new Date().getTime().toString(),
  )
})

function manageState() {
  if (newUser.value === null || !newUser.value.id) return

  if (state.value === 0) {
    // Start AB Test 'curator-onboarding'
    if (isFeatureOnForGroup($growthBook, 'curator-onboarding', 'new-flow')) {
      return navigateTo(
        localePath({
          path: `/influencer/signup/v2/${CURRENT_STEP.value}`,
        }),
      )
    } else {
      return (state.value = 1)
    }
    // End AB Test 'curator-onboarding'
  }

  if (newInfluencer !== null) if (state.value === 1) state.value = 2
}

function incrementState(): void {
  state.value += 1
}

function getTagNamesFromTagObject(
  tagObject: Record<string, number[]>,
): string[] {
  return Object.values(tagObject).reduce((output, tagIds) => {
    tagIds.forEach((tagId) => {
      const tag = GET_TAG_FROM_ID(tagId)
      if (tag) output.push(tag.name)
    })
    return output
  }, [] as string[])
}

function getTagNamesFromIdList(idList: number[]): string[] {
  const modifications = {
    _all_: 'any',
    _none_: 'instrumental-only',
  } as Record<string, string>
  return GET_TAGS_FROM_IDS(idList).map((tag: Tag) =>
    tag.name in modifications ? modifications[tag.name] : tag.name,
  )
}

function handleConfirmModifications({ index }: { index: number }): void {
  if (index === 2) trackGenresSelectionsDone()

  if (index === 3) trackLanguagesAndLocalizationSelection()

  if (index === 4) trackReleasePreferenceAddition()
}

function handleNewInfluencerUpdate(state: number) {
  if (state === 2) trackCuratorPersonalSiteAddition()

  if (state === 4) trackSimilarArtistsSelection()
}

async function finalize(curatorPicture: string | null): Promise<void> {
  cleanCacheAll()
  if (!import.meta.client || !newUser.value || !newInfluencer.value) return

  window.localStorage.setItem('displayInfluencerValidationMessage', 'true')
  window.localStorage.setItem('needsWarmWelcome', 'true')
  USER_SET({ ...newUser.value })
  USER_INFLUENCER_SET({ ...newInfluencer.value })
  const refreshCoreCuratorCache = provideCuratorRefreshCache(coreFetch)
  trackProfilePictureFinalized(curatorPicture)
  await refreshCoreCuratorCache(newInfluencer.value.id)
  await navigateTo(localePath('/influencer/signup/discovery/'))
}

function trackProfilePictureFinalized(curatorPicture: string | null): void {
  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
  })
  segmentTrack('Curator Onboarding - Profile Picture Finalized', {
    curator_id: INFLUENCER_ID.value,
    has_modified_profile_picture_curator: Boolean(curatorPicture),
  })
}

function trackCuratorPersonalSiteAddition(): void {
  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
    curator_website: newInfluencer.value?.link,
  })
  segmentTrack('Curator Onboarding - Website Step Finalized', {
    curator_id: INFLUENCER_ID.value,
    curator_website: newInfluencer.value?.link ?? '',
  })
}

function trackSimilarArtistsSelection(): void {
  const similarArtists = (newInfluencer.value?.similar_artists ?? []).map(
    (artist) => artist.name,
  )
  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
    curator_similar_artists_list: similarArtists,
  })
  segmentTrack('Curator Onboarding - Similar Artists Step Finalized', {
    curator_id: INFLUENCER_ID.value,
    curator_similar_artists_list: similarArtists,
    has_modified_similar_artists: Boolean(similarArtists.length),
  })
}

function trackGenresSelectionsDone(): void {
  const hatedGenres = getTagNamesFromTagObject(
    INFLUENCER_TAGS.value.hated ?? {},
  )
  const likedGenres = getTagNamesFromTagObject(
    INFLUENCER_TAGS.value.neutral ?? {},
  )
  const lovedGenres = getTagNamesFromTagObject(
    INFLUENCER_TAGS.value.liked ?? {},
  )
  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
    curator_hated_genres_list: hatedGenres,
    curator_liked_genres_list: likedGenres,
    curator_loved_genres_list: lovedGenres,
  })
  const hasModifiedGenres = Boolean(
    hatedGenres.length + likedGenres.length + lovedGenres.length,
  )
  segmentTrack('Curator Onboarding - Genres Step Finalized', {
    curator_id: INFLUENCER_ID.value,
    has_modified_genres: hasModifiedGenres,
    curator_hated_genres_list: hatedGenres,
    curator_liked_genres_list: likedGenres,
    curator_loved_genres_list: lovedGenres,
  })
}

function trackLanguagesAndLocalizationSelection(): void {
  const { exclusivity: exclusivityTags } = INFLUENCER_TAGS.value

  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
    languages_to_receive_list: getTagNamesFromIdList(
      exclusivityTags.lyrics_lang,
    ),
    countries_to_receive_list: getTagNamesFromIdList(exclusivityTags.country),
  })
  segmentTrack('Curator Onboarding - Localization Preferences Step Finalized', {
    curator_id: INFLUENCER_ID.value,
    languages_to_receive_list: getTagNamesFromIdList(
      exclusivityTags.lyrics_lang,
    ),
    countries_to_receive_list: getTagNamesFromIdList(exclusivityTags.country),
  })
}

function trackReleasePreferenceAddition(): void {
  const releasePreferenceId = INFLUENCER_TAGS.value.exclusivity.track_age[0]
  const releasePreference = {
    738: 'any',
    637: 'released',
    638: 'unreleased',
  }[releasePreferenceId]

  if (!releasePreference) return

  segmentIdentify({
    curator_name: newInfluencer.value?.entity,
    curator_release_preference: releasePreference,
  })
  segmentTrack('Curator Onboarding - Release Preference Step Finalized', {
    curator_id: INFLUENCER_ID.value,
    curator_release_preference: releasePreference,
  })
}

async function handlePictureUpload() {
  const patchCurator = providePatchCurator(coreFetch)
  await patchCurator(INFLUENCER_ID.value, {
    has_profile_picture: true,
    has_banner: true,
  })
}

watch(newUser, manageState, { deep: true })
watch(newInfluencer, manageState, { deep: true })
watch(state, () => {
  trackEvent(
    {
      category: 'Influencer Signup',
      action: 'progress',
      label: state.value.toString(),
    },
    route,
  )
})
</script>

<template>
  <div class="tw-overflow mainWrapPage">
    <div id="ignoreFontUpscale" class="ignoreFontUpscale mainParentContainer">
      <transition name="fade" mode="out-in">
        <div v-if="state === 0" :key="0" class="firstSlide">
          <div class="leftFlyer">
            <div
              class="mainTitle"
              :style="{
                backgroundImage: `url(${getMediaUrl('rectangleInfSignup.svg')})`,
              }"
            >
              <span class="preWrap">{{ $t('influencer.signup00') }}</span>
            </div>

            <div class="pointWrapper">
              <div class="glass">
                <img :src="getMediaUrl('whiteGlass.svg')" />
                <div class="subTitle">
                  {{ $t('influencer.signup01') }}
                </div>
                <div class="text">
                  {{ $t('influencer.signup02') }}
                </div>
              </div>
              <div class="mail">
                <img :src="getMediaUrl('Mails.svg')" />
                <div class="subTitle">
                  {{ $t('influencer.signup03') }}
                </div>
                <div class="text">
                  {{ $t('influencer.signup04') }}
                </div>
              </div>
              <div class="grooviz">
                <img :src="getMediaUrl('Grooviz-light.svg')" />
                <div class="subTitle">
                  {{ $t('influencer.signup05') }}
                </div>
                <div class="text">
                  {{ $t('influencer.signup06') }}
                </div>
              </div>
              <a
                class="moreQuestions"
                :href="$t('links.signupInfluencerFaq')"
                target="_blank"
                >{{ $t('influencer.signup07') }}</a
              >
            </div>
            <img
              class="floatyShape l"
              alt=""
              :src="getMediaUrl('infSignupShapeBeige.svg')"
            />
            <img
              class="floatyShape r"
              alt=""
              :src="getMediaUrl('infSignupShapeBlue.svg')"
            />
          </div>
          <div
            class="rightFlyer"
            :style="{
              backgroundImage: `url(${getMediaUrl('infSignupVinyles.svg')})`,
            }"
          >
            <AuthCreateBaseUser v-model="newUser" class="tw-mb-6" />
          </div>
        </div>
        <div v-else :key="1" class="futureSteps mainParentContainer">
          <div class="progressionBar">
            <div v-for="n in 5" :key="n" class="progBarWrap">
              <div
                class="point"
                :data-test-id="
                  state === n ? 'curatorSignupStepCurrentNumber' : null
                "
                :class="{
                  current: state === n,
                  past: state > n,
                  future: state < n,
                }"
              >
                {{ n }}
              </div>
              <div v-if="n < 5" class="line" />
            </div>
          </div>
          <transition name="fade" mode="out-in">
            <div v-if="state === 1" :key="1" class="stepHolder">
              <InfluencerCreateBase
                v-if="!!newUser"
                v-model="newInfluencer"
                :user="newUser"
              />
            </div>
            <div v-else-if="state === 2" :key="2" class="stepHolder">
              <InfluencerCreateLink
                v-if="!!newInfluencer"
                v-model="newInfluencer"
                @request-done="
                  (handleNewInfluencerUpdate(state), incrementState())
                "
              />
            </div>
            <div v-else-if="state === 3" :key="3" class="stepHolder">
              <div class="title">
                {{ $t('influencer.signup10') }}
              </div>
              <div class="subtitle">
                {{ $t('influencer.signup11') }}
              </div>
              <InfluencerTagsSelectPreference
                @request-done="
                  (handleConfirmModifications({ index: 4 }), incrementState())
                "
                @confirm-modifications="handleConfirmModifications"
              />
            </div>
            <div v-else-if="state === 4" :key="4" class="stepHolder">
              <InfluencerCreateFavoriteArtists
                v-if="!!newInfluencer"
                :model-value="newInfluencer"
                @update:model-value="
                  newInfluencer = { ...newInfluencer, ...$event }
                "
                @request-done="
                  (handleNewInfluencerUpdate(state), incrementState())
                "
              />
            </div>
            <div v-else-if="state === 5" :key="5" class="stepHolder pictureBoi">
              <div class="title">
                {{ $t('influencer.signup08') }}
              </div>
              <div class="subtitle">
                {{ $t('influencer.signup09') }}
              </div>
              <div class="pictureCompOuterWrap">
                <UploadPictureCard
                  v-if="newInfluencer && newUser"
                  :pk="newUser.id"
                  user-kind="influencer"
                  :name="newInfluencer.entity"
                  :slug="newInfluencer.slug"
                  @export-picture-data-url="finalize"
                  @influencer-profile-picture-uploaded="handlePictureUpload"
                />
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </div>
    <div class="mainParentContainer ignoreFontUpscale">
      <SeoFooter :lists="seoListing" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.firstSlide {
  @apply tw-relative tw-grid tw-grid-cols-1 tw-gap-6;

  @screen lg {
    @apply tw-grid-cols-2;
  }
}

.mainWrapPage {
  @apply tw-w-screen tw-overflow-hidden;
}

.influencerSignupWrapper {
  @apply tw-relative tw-mx-auto tw-my-0 tw-w-full tw-max-w-7xl;
}

.firstSlide .mainTitle {
  position: absolute;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  width: 100%;
  height: 166px;
  left: 0px;
  top: 0px;
  z-index: -1;
}

.firstSlide .mainTitle {
  position: relative;
  width: 100%;
  padding-top: 49px;
  text-align: left;
  z-index: 2;
  margin-bottom: 20px;
  padding-left: 100px;
}

.firstSlide .mainTitle > span {
  color: var(--color-white);

  font-weight: 700;
  font-size: var(--font-size-title);
  max-width: 464px;
  display: block;
  margin: 0 auto;
}

.firstSlide > div {
  width: 100%;
}

.firstSlide .leftFlyer {
  max-width: 566px;
  margin-left: auto;
  justify-self: right;
  background-color: var(--color-white);
  min-height: 730px;
  z-index: 0;
  position: relative;
}

.firstSlide .rightFlyer {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 26px;
}

.firstSlide .rightFlyer {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-repeat: no-repeat;
  background-position: top 26px left 0;
  background-size: contain;
}

.firstSlide .rightFlyer > div {
  max-width: 380px;
}

.firstSlide .leftFlyer .pointWrapper .subTitle {
  color: var(--color-black);
  font-size: var(--font-size-big);
  font-weight: 600;

  margin-bottom: 4px;
  overflow: hidden;
}

.firstSlide .leftFlyer .pointWrapper .text {
  color: var(--color-masala-grey-h);
  line-height: 1.71;
  font-size: var(--font-size-small);
  width: 100%;
  overflow: hidden;
}

.moreQuestions {
  color: var(--color-param);
  font-style: italic;
  font-size: var(--font-size-small);
  margin: 0px auto;
  max-width: 365px;
  width: 100%;
  display: block;
  cursor: pointer;
}

.pointWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: stretch;
  gap: 40px;
}

.pointWrapper > div {
  position: relative;
  max-width: 365px;
}

.glass > img,
.mail > img,
.grooviz > img {
  position: absolute;
  top: -13px;
  left: -45px;
  width: 64px;
  height: 64px;
  z-index: -1;
}

.stepHolder {
  @apply tw-mx-auto tw-mb-16 tw-grid tw-min-h-[512px] tw-w-full tw-max-w-lg tw-items-center tw-justify-center;
}

.stepHolder .title {
  font-size: var(--font-size-title-big);
  color: var(--color-black);

  margin: 0px auto 4px auto;
  min-height: 46px;
  font-weight: 700;
  text-align: center;
}

.stepHolder .subtitle {
  min-height: 54px;
  color: var(--color-masala-grey-h);
  font-size: var(--font-size-big);
  text-align: center;
  max-width: 437px;
  margin: 0px auto 40px auto;
}

.pictureBoi .subtitle {
  margin: 0px auto 120px auto;
}

.futureSteps {
  position: relative;
}

.progressionBar {
  position: absolute;
  width: 28px;
  top: 0px;
  left: calc(50% - (512px / 1.25));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  @apply tw-pt-12;
}

.progressionBar .point {
  left: calc((100% - 512px) / 4);
  width: 28px;
  height: 28px;
  border-radius: 28px;
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 18px;
  font-weight: 700;
  transition: 0.15s var(--trans-standart);
}

.progressionBar .point.current {
  background-color: var(--color-main);
  color: var(--color-white);
  transition: 0.1s var(--trans-decelerate);
}

.progressionBar .point.past {
  color: var(--color-main);
  background-color: var(--color-ebb-grey);
}

.progressionBar .point.future {
  color: var(--color-main);
  background-color: var(--color-ebb-grey);
  opacity: 0.1;
}

.line {
  height: 70px;
  width: 1px;
  background-color: var(--color-ebb-grey);
  opacity: 0.6;
}

.floatyShape {
  position: absolute;
  bottom: 0px;
  object-fit: contain;
}

.floatyShape.l {
  left: 0px;
  height: 100px;
  width: 275px;
}

.floatyShape.r {
  right: 0px;
  bottom: 0px;
  width: 178px;
  height: 98px;
}

.progBarWrap {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

@media screen and (max-width: 767px) {
  .firstSlide {
    flex-wrap: wrap;
    width: 100%;
  }

  .firstSlide .mainTitle:before {
    height: 266px;
  }

  .firstSlide .leftFlyer,
  .firstSlide .rightFlyer {
    max-width: unset;
    position: relative;
  }

  .moreQuestions {
    text-align: center;
    margin-bottom: 134px;
    max-width: unset;
  }

  .leftFlyer > .pointWrapper {
    padding: 0px var(--grid-pad-adjust);
  }

  .firstSlide .mainTitle {
    height: 216px;
    padding-left: var(--grid-pad-adjust);
    padding-right: var(--grid-pad-adjust);
    width: auto;
  }

  .firstSlide .leftFlyer .pointWrapper {
    max-width: 555px;
  }

  .stepHolder,
  .kindSelectionWrapper,
  .nameSelectionWrapper,
  .genreSelectionWrapper,
  .pictureTime {
    max-width: unset;
    width: 100%;
    padding-left: var(--grid-pad-adjust);
    padding-right: var(--grid-pad-adjust);
  }
}

@media screen and (max-width: 1023px) {
  .firstSlide .leftFlyer {
    max-width: unset;
  }
}
</style>
